/**
 * ExposureDashboard - Risk/Exposure dashboard for position transparency
 *
 * Features:
 * - LP delta exposure display
 * - Hedge delta display
 * - Net delta (LP + hedge) with color-coded health indicator
 * - Position health status (green/yellow/red)
 * - Volatility regime indicator
 * - Collapsible on mobile, expanded on desktop
 * - Real-time updates (polls every 30s)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiService, { ExposureData, PositionHealth, VolatilityRegime } from '../../services/api';

// Polling interval in milliseconds (30 seconds)
const POLL_INTERVAL = 30000;

interface ExposureDashboardProps {
  className?: string;
  defaultExpanded?: boolean;
  variant?: 'mobile' | 'desktop';
}

// Health color mapping
const getHealthColor = (health: PositionHealth): string => {
  switch (health) {
    case 'healthy':
      return 'text-green-400';
    case 'warning':
      return 'text-yellow-400';
    case 'critical':
      return 'text-red-400';
    case 'inactive':
      return 'text-gray-500';
    default:
      return 'text-gray-400';
  }
};

const getHealthBgColor = (health: PositionHealth): string => {
  switch (health) {
    case 'healthy':
      return 'bg-green-500/20 border-green-500/30';
    case 'warning':
      return 'bg-yellow-500/20 border-yellow-500/30';
    case 'critical':
      return 'bg-red-500/20 border-red-500/30';
    case 'inactive':
      return 'bg-gray-500/20 border-gray-500/30';
    default:
      return 'bg-gray-500/20 border-gray-500/30';
  }
};

const getHealthLabel = (health: PositionHealth): string => {
  switch (health) {
    case 'healthy':
      return 'Well Hedged';
    case 'warning':
      return 'Partial Exposure';
    case 'critical':
      return 'High Exposure';
    case 'inactive':
      return 'Inactive';
    default:
      return 'Unknown';
  }
};

// Volatility regime display
const getVolatilityLabel = (regime: VolatilityRegime): string => {
  switch (regime) {
    case 'low':
      return 'Low';
    case 'normal':
      return 'Normal';
    case 'high':
      return 'High';
    case 'extreme':
      return 'Extreme';
    default:
      return 'Unknown';
  }
};

const getVolatilityColor = (regime: VolatilityRegime): string => {
  switch (regime) {
    case 'low':
      return 'text-blue-400';
    case 'normal':
      return 'text-green-400';
    case 'high':
      return 'text-yellow-400';
    case 'extreme':
      return 'text-red-400';
    default:
      return 'text-gray-400';
  }
};

// Format delta value with sign (SOL units)
const formatDelta = (value: number): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(4)} SOL`;
};

// Format USD value with sign
const formatDeltaUsd = (value: number | undefined): string => {
  if (value === undefined) return '';
  const sign = value >= 0 ? '+' : '-';
  return `${sign}$${Math.abs(value).toFixed(2)}`;
};

// Delta metric component - displays SOL as primary with USD in parentheses
interface DeltaMetricProps {
  label: string;
  value: number;
  valueUsd?: number;
  description?: string;
  className?: string;
}

const DeltaMetric: React.FC<DeltaMetricProps> = ({
  label,
  value,
  valueUsd,
  description,
  className = '',
}) => {
  const isPositive = value >= 0;
  const valueColor = Math.abs(value) < 0.0001 ? 'text-gray-400' : isPositive ? 'text-green-400' : 'text-red-400';

  return (
    <div className={`flex flex-col ${className}`}>
      <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
      <div className="flex items-baseline gap-2">
        <span className={`text-lg font-semibold ${valueColor}`}>
          {formatDelta(value)}
        </span>
        {valueUsd !== undefined && (
          <span className="text-sm text-gray-500">
            ({formatDeltaUsd(valueUsd)})
          </span>
        )}
      </div>
      {description && (
        <span className="text-xs text-gray-600 mt-0.5">{description}</span>
      )}
    </div>
  );
};

// Mobile collapsible version
const MobileExposureDashboard: React.FC<{
  data: ExposureData | null;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
}> = ({ data, isLoading, error, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!data) {
    return null;
  }

  const health = data.position_health;
  const healthColor = getHealthColor(health);
  const healthBgColor = getHealthBgColor(health);

  return (
    <motion.div
      className={`rounded-xl border ${healthBgColor} overflow-hidden`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Collapsed header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          {/* Health indicator dot */}
          <div className={`w-2.5 h-2.5 rounded-full ${
            health === 'healthy' ? 'bg-green-400' :
            health === 'warning' ? 'bg-yellow-400' :
            health === 'critical' ? 'bg-red-400' :
            'bg-gray-400'
          }`} />
          <div className="text-left">
            <span className="text-sm font-medium text-white">Risk Exposure</span>
            <span className={`ml-2 text-xs ${healthColor}`}>
              {getHealthLabel(health)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Net delta quick view */}
          <span className={`text-sm font-mono ${
            Math.abs(data.net_delta) < 0.01 ? 'text-green-400' : 'text-yellow-400'
          }`}>
            {formatDelta(data.net_delta)}
          </span>
          {/* Expand icon */}
          <motion.svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            animate={{ rotate: isExpanded ? 180 : 0 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </motion.svg>
        </div>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pb-4 space-y-4 border-t border-gray-700/50">
              {/* Delta breakdown */}
              <div className="grid grid-cols-2 gap-4 pt-4">
                <DeltaMetric
                  label="LP Delta"
                  value={data.lp_delta}
                  valueUsd={data.lp_delta_usd}
                  description="From LP positions"
                />
                <DeltaMetric
                  label="Hedge Delta"
                  value={data.hedge_delta}
                  valueUsd={data.hedge_delta_usd}
                  description="Drift perp short"
                />
              </div>

              {/* Net delta (prominent) */}
              <div className={`p-3 rounded-lg ${healthBgColor}`}>
                <DeltaMetric
                  label="Net Delta"
                  value={data.net_delta}
                  valueUsd={data.net_delta_usd}
                  description={`${data.net_delta_percent?.toFixed(1) || 0}% of notional`}
                />
              </div>

              {/* Additional info */}
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Volatility:</span>
                  <span className={getVolatilityColor(data.volatility_regime)}>
                    {getVolatilityLabel(data.volatility_regime)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Positions:</span>
                  <span className="text-white">{data.positions_count || 0}</span>
                </div>
              </div>

              {/* Refresh button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh();
                }}
                disabled={isLoading}
                className="w-full py-2 text-sm text-gray-400 hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <motion.span
                      className="w-4 h-4 border-2 border-gray-500 border-t-white rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                    Updating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>

              {error && (
                <p className="text-xs text-red-400 text-center">{error}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Desktop expanded version
const DesktopExposureDashboard: React.FC<{
  data: ExposureData | null;
  isLoading: boolean;
  error: string | null;
  onRefresh: () => void;
}> = ({ data, isLoading, error, onRefresh }) => {
  if (!data) {
    return (
      <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Risk Exposure</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          {isLoading ? 'Loading exposure data...' : 'No exposure data available'}
        </div>
      </div>
    );
  }

  const health = data.position_health;
  const healthColor = getHealthColor(health);
  const healthBgColor = getHealthBgColor(health);

  return (
    <motion.div
      className="bg-gray-900/50 rounded-xl p-6 border border-gray-800"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-white">Risk Exposure</h3>
          {/* Health badge */}
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${healthBgColor} ${healthColor}`}>
            {getHealthLabel(health)}
          </span>
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
        >
          {isLoading ? (
            <motion.svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </motion.svg>
          ) : (
            <svg className="w-5 h-5 text-gray-400 hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          )}
        </button>
      </div>

      {/* Delta metrics grid */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        <DeltaMetric
          label="LP Delta"
          value={data.lp_delta}
          valueUsd={data.lp_delta_usd}
          description="Long exposure from LP"
        />
        <DeltaMetric
          label="Hedge Delta"
          value={data.hedge_delta}
          valueUsd={data.hedge_delta_usd}
          description="Short exposure from Drift"
        />
        <div className={`p-4 -m-4 rounded-lg ${healthBgColor}`}>
          <DeltaMetric
            label="Net Delta"
            value={data.net_delta}
            valueUsd={data.net_delta_usd}
            description={`${data.net_delta_percent?.toFixed(1) || 0}% of notional`}
          />
        </div>
      </div>

      {/* Additional metrics */}
      <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-800">
        <div>
          <span className="text-xs text-gray-500 uppercase">LP Notional</span>
          <p className="text-sm font-medium text-white">
            ${data.lp_notional?.toLocaleString() || '0'}
          </p>
        </div>
        <div>
          <span className="text-xs text-gray-500 uppercase">Volatility</span>
          <p className={`text-sm font-medium ${getVolatilityColor(data.volatility_regime)}`}>
            {getVolatilityLabel(data.volatility_regime)}
          </p>
        </div>
        <div>
          <span className="text-xs text-gray-500 uppercase">Positions</span>
          <p className="text-sm font-medium text-white">
            {data.positions_count || 0} active
          </p>
        </div>
        <div>
          <span className="text-xs text-gray-500 uppercase">SOL Price</span>
          <p className="text-sm font-medium text-white">
            ${data.sol_price?.toFixed(2) || '0'}
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-400 mt-4">{error}</p>
      )}

      {/* Last updated */}
      <p className="text-xs text-gray-600 mt-4">
        Last updated: {new Date(data.last_updated).toLocaleTimeString()}
      </p>
    </motion.div>
  );
};

// Main component with data fetching
const ExposureDashboard: React.FC<ExposureDashboardProps> = ({
  className = '',
  defaultExpanded = false,
  variant = 'mobile',
}) => {
  const [data, setData] = useState<ExposureData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchExposure = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const exposureData = await apiService.vault.getExposure();
      setData(exposureData);
    } catch (err: any) {
      console.error('Failed to fetch exposure data:', err);
      // Don't show error for expected states (bot not running, etc)
      if (err.response?.status === 503) {
        // Service unavailable - bot probably not running
        setData({
          status: 'unavailable',
          message: 'Could not fetch exposure data',
          lp_delta: 0,
          hedge_delta: 0,
          net_delta: 0,
          position_health: 'unknown',
          volatility_regime: 'unknown',
          positions: [],
          last_updated: new Date().toISOString(),
        });
      } else {
        setError('Failed to load exposure data');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchExposure();
  }, [fetchExposure]);

  // Polling
  useEffect(() => {
    const interval = setInterval(fetchExposure, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchExposure]);

  const handleRefresh = () => {
    fetchExposure();
  };

  // Don't render if status indicates no data
  if (data?.status === 'no_bot' || data?.status === 'bot_stopped') {
    return null;
  }

  return (
    <div className={className}>
      {variant === 'mobile' ? (
        <MobileExposureDashboard
          data={data}
          isLoading={isLoading}
          error={error}
          onRefresh={handleRefresh}
        />
      ) : (
        <DesktopExposureDashboard
          data={data}
          isLoading={isLoading}
          error={error}
          onRefresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default ExposureDashboard;
